import React, {useEffect, useState} from 'react';
import {Alert, Button, Skeleton, Space, Typography} from 'antd';
import {useTranslation} from 'react-i18next';
import api from '../api';
import TextAssetFields from './TextAssetFields';
import ConfigurableEntityIcon from './ConfigurableEntityIcon';
import {CUSTOM_META_FIELDS_CHANGED_EVENT} from '../helpers/customMetaFieldsEvents';

export default function WorkspaceTextAssetForm({selectedAssetIds, selectedAsset, organization}) {
    const {t} = useTranslation();
    const assetId = selectedAssetIds.length === 1 ? selectedAssetIds[0] : null;
    const [requestVersion, setRequestVersion] = useState(0);
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        const refresh = event => {
            if (event.type === CUSTOM_META_FIELDS_CHANGED_EVENT && event.detail?.ownerType && event.detail.ownerType !== 'Asset') return;
            if (event.type === 'mediagraph:dataRefresh' && !event.detail?.scopes?.includes('assets')) return;
            setRequestVersion(version => version + 1);
        };
        window.addEventListener(CUSTOM_META_FIELDS_CHANGED_EVENT, refresh);
        window.addEventListener('mediagraph:dataRefresh', refresh);
        return () => {
            window.removeEventListener(CUSTOM_META_FIELDS_CHANGED_EVENT, refresh);
            window.removeEventListener('mediagraph:dataRefresh', refresh);
        };
    }, []);

    useEffect(() => {
        let active = true;
        setDetail(null);
        setError(false);
        if (!assetId) return;
        api.get(`/api/assets/${assetId}`).then(async response => {
            if (!active) return;
            const asset = response.data;
            setDetail({assetId, organization, asset, contentLoading: !!asset.entity_type});
            if (!asset.entity_type) return;
            try {
                const content = (await api.get(`/api/assets/${assetId}/content`, {responseType: 'text'})).data;
                if (active) setDetail({assetId, organization, asset, content});
            } catch {
                // Main Text has its own permission. Retain the fields that the
                // detail endpoint already authorized when content is unavailable.
                if (active) setDetail({assetId, organization, asset, contentError: true});
            }
        }).catch(() => { if (active) setError(true); });
        return () => { active = false; };
    }, [assetId, selectedAsset, organization, requestVersion]);

    if (!assetId) return <Typography.Text type="secondary">{t('select-one-text-asset', 'Select one text asset to view its form.')}</Typography.Text>;
    if (error) return <Alert type="error" message={t('text-asset-form-load-error', 'This asset could not be loaded.')} action={<Button size="small" onClick={() => setRequestVersion(version => version + 1)}>{t('retry', 'Retry')}</Button>}/>;
    if (detail?.assetId !== assetId || detail?.organization !== organization) return <Skeleton active/>;
    if (!detail.asset.entity_type) return <Typography.Text type="secondary">{t('selected-asset-has-no-form', 'The selected asset has no text asset form.')}</Typography.Text>;
    const {asset, content} = detail;
    return <Space direction="vertical" size="middle" style={{width: '100%'}} data-testid="workspace-text-asset-form">
        <Typography.Text type="secondary"><ConfigurableEntityIcon icon={asset.entity_type.icon} kind="text_asset"/>{asset.entity_type.name}</Typography.Text>
        <Typography.Title level={5} style={{margin: 0, overflowWrap: 'anywhere'}}>{asset.display_title || asset.filename}</Typography.Title>
        <TextAssetFields asset={asset} layout="vertical"/>
        <div style={{width: '100%'}}>
            <Typography.Title level={5}>{t('main-text', 'Main Text')}</Typography.Title>
            {detail.contentLoading ? <Skeleton active paragraph={{rows: 3}}/> : detail.contentError
                ? <Alert type="info" message={t('main-text-unavailable', 'Main Text is unavailable.')} action={<Button size="small" onClick={() => setRequestVersion(version => version + 1)}>{t('retry', 'Retry')}</Button>}/>
                : <div data-testid="workspace-main-text" style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{content || '—'}</div>}
        </div>
    </Space>;
}
